import {Dialog, PortalManager, showNotification} from 'platform/components';
import {LightboxAction, useLightboxActionsFactory} from 'platform/lightbox';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

import {PhotoDownloadSizeDialog} from '../components/PhotoDownloadSizeDialog/PhotoDownloadSizeDialog';
import {PhotoDownloadSizePreset} from '../constants/photoDownloadSizePresets';
import {downloadCompressedPhoto} from '../utils/downloadCompressedPhoto';
import {usePhotoDownloadSizePreset} from './usePhotoDownloadSizePreset';

const PHOTO_DOWNLOAD_SIZE_DIALOG_ID = 'photo-download-size-dialog';

interface DmsLightboxAsset {
  id: string;
  url?: string | Nullish;
  fileName?: string | Nullish;
}

interface PendingPhotoDownload {
  url: string;
  fileName?: string | Nullish;
}

type CustomActions<T extends DmsLightboxAsset> =
  LightboxAction[] | ((asset: T) => LightboxAction[]);

const resolveCustomActions = <T extends DmsLightboxAsset>(
  asset: T,
  customActions: CustomActions<T> | undefined
) => (typeof customActions === 'function' ? customActions(asset) : (customActions ?? []));

/**
 * `useLightboxActionsFactory` for DMS photos: the download action first asks for the maximum photo
 * size and downloads the photo compressed to it.
 *
 * Render `photoDownloadSizeDialog` next to the lightbox. It is a declarative dialog with its own
 * portal on `document.body`, so it shows above the lightbox. An imperative `openDialog` dialog
 * mounts in the dialogs provider stacking context, which is below the lightbox.
 */
export function useDmsLightboxActionsFactory() {
  const lightboxActionsFactory = useLightboxActionsFactory();
  const [rememberedPreset, setRememberedPreset] = usePhotoDownloadSizePreset();
  const [pendingDownload, setPendingDownload] = useState<PendingPhotoDownload | null>(null);
  // Dialog content key: a new key per open discards the unconfirmed selection of the previous open.
  const [dialogOpenCount, setDialogOpenCount] = useState(0);

  const closeDialog = () => setPendingDownload(null);

  const handleConfirm = (preset: PhotoDownloadSizePreset) => {
    setRememberedPreset(preset);
    closeDialog();

    if (isNil(pendingDownload)) {
      return;
    }

    downloadCompressedPhoto(pendingDownload.url, preset, pendingDownload.fileName).catch(() =>
      showNotification.error(i18n.t('entity.photo.notifications.downloadFailed'))
    );
  };

  const createDownloadAction = <T extends DmsLightboxAsset>(asset: T): LightboxAction => ({
    id: `downloadBtn-${asset.id}`,
    icon: 'content/save_alt',
    onClick: () => {
      const url = asset.url;
      if (isNil(url)) {
        return;
      }

      setDialogOpenCount((count) => count + 1);
      setPendingDownload({url, fileName: asset.fileName});
    },
  });

  const photoDownloadSizeDialog = (
    // No zIndex: resets an inherited dialogs provider z-index that would keep the dialog below the lightbox.
    <PortalManager>
      <Dialog
        isOpen={isNotNil(pendingDownload)}
        isInLightbox
        size="small"
        title={i18n.t('entity.photo.labels.downloadSize')}
        onClose={closeDialog}
        withAdditionalFooter
        data-testid={PHOTO_DOWNLOAD_SIZE_DIALOG_ID}
      >
        <PhotoDownloadSizeDialog
          key={dialogOpenCount}
          defaultPreset={rememberedPreset}
          onConfirm={handleConfirm}
          onCancel={closeDialog}
          data-testid={PHOTO_DOWNLOAD_SIZE_DIALOG_ID}
        />
      </Dialog>
    </PortalManager>
  );

  return {
    photoDownloadSizeDialog,
    addShareOnlyActions: lightboxActionsFactory.addShareOnlyActions,
    addFullActions: <T extends DmsLightboxAsset>(
      assets?: T[] | Nullish,
      customActions?: CustomActions<T>
    ) =>
      lightboxActionsFactory.addCustomActions(assets, {
        allowShare: true,
        allowDownload: false,
        // The platform factory places download before custom actions; keep that order.
        customActions: (asset: T) => [
          createDownloadAction(asset),
          ...resolveCustomActions(asset, customActions),
        ],
      }),
  };
}
